import { v4 as uuid } from 'uuid';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { blocked } from 'flare-chat-core/contracts/chat/operation-result';
import type { ChatOperationResult } from 'flare-chat-core/contracts';
import type { ArtifactEditCommand, ProjectArtifactAPI, SaveMeta, SaveReceipt, SaveStatus } from './contracts';
import { buildCanvasDraftSaveAppendPayload } from '../../adapters/artifacts/draft-save.js';
import { commitCanvasDraftAppend } from './commit.js';

interface SaveRecord {
  command: ArtifactEditCommand; receipt?: SaveReceipt; result?: ChatOperationResult<SaveReceipt>;
  promise?: Promise<ChatOperationResult<SaveReceipt>>;
}
export const useSave = ({ sessionId, projectAPI, refresh, onCommitted }: {
  sessionId: string; projectAPI: Partial<ProjectArtifactAPI>; refresh: () => Promise<unknown>;
  onCommitted?: (acknowledgment: import('./contracts').ArtifactEditResponse, expectedBase: import('./contracts').ArtifactRef) => Promise<unknown>;
}) => {
  // Changing credentials/server creates a different API object and cannot reuse old acknowledgments.
  const recordScope = useMemo(() => ({ projectAPI, records: new Map<string, SaveRecord>() }), [projectAPI]);
  const { records } = recordScope;
  const [status, setStatus] = useState<SaveStatus>({ pending: false, result: null });
  const scope = useMemo(() => ({ sessionId, projectAPI, active: true,
    requests: new Map<string, AbortController>() }), [sessionId, projectAPI]);
  const current = useRef(scope);
  current.current = scope;
  useEffect(() => {
    scope.active = true;
    setStatus({ pending: false, result: null });
    return () => { scope.active = false; [...scope.requests.values()].forEach((entry) => entry.abort()); };
  }, [scope]);

  const save = useCallback((draftText: string, meta: SaveMeta): Promise<ChatOperationResult<SaveReceipt>> => {
    if (!scope.active || current.current !== scope) return Promise.resolve(blocked('artifact_scope_changed'));
    let command: ArtifactEditCommand;
    try {
      command = buildCanvasDraftSaveAppendPayload({ artifactRef: meta.artifactRef,
        draftText, sessionId, requestId: uuid() });
    } catch {
      return Promise.resolve(blocked('artifact_reference_unavailable'));
    }
    const key = JSON.stringify([sessionId, command.expected_base, draftText]);
    const previous = records.get(key);
    if (previous?.promise) return previous.promise;
    if (previous?.result?.status === 'completed') return Promise.resolve(previous.result);
    const record = previous ?? { command };
    records.set(key, record);
    const resource = JSON.stringify([record.command.expected_base.scope,
      record.command.expected_base.artifact_type, record.command.expected_base.artifact_id]);
    scope.requests.get(resource)?.abort();
    const controller = new AbortController();
    scope.requests.set(resource, controller);
    const isCurrent = () => scope.active && current.current === scope && !controller.signal.aborted
      && scope.requests.get(resource) === controller;
    setStatus({ pending: true, result: previous?.result ?? null });
    record.promise = commitCanvasDraftAppend({ activeSessionId: sessionId, appendPayload: record.command,
      projectAPI, acknowledgedReceipt: record.receipt, signal: controller.signal, isCurrent,
      onArtifactEditCommitted: (acknowledgment: import('./contracts').ArtifactEditResponse) =>
        onCommitted ? onCommitted(acknowledgment, record.command.expected_base) : refresh(),
    }).then((result: ChatOperationResult<SaveReceipt>) => {
      record.result = result;
      if ('value' in result && result.value?.acknowledgment) record.receipt = result.value;
      if (isCurrent()) {
        scope.requests.delete(resource);
        setStatus({ pending: scope.requests.size > 0, result });
      }
      return result;
    }).finally(() => {
      record.promise = undefined;
      if (scope.requests.get(resource) === controller) scope.requests.delete(resource);
    });
    return record.promise;
  }, [projectAPI, refresh, onCommitted, records, scope, sessionId]);
  return { save, status };
};
